import { useState } from "react";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Link } from "react-router-dom";
import { 
  FileText, 
  Download, 
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Loader2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// FAQ data for JSON-LD and display
const faqData = [
  {
    question: "What is the Model AI Governance Framework (MGF) for Agentic AI?",
    answer: "A framework published by Singapore's IMDA and the AI Verify Foundation, first released in January 2026 and updated in May 2026. It is the first national governance framework written specifically for AI agents that plan, reason, and act autonomously, organised around four dimensions: risk assessment, human accountability, technical controls, and end-user responsibility."
  },
  {
    question: "How is governing an AI agent different from governing an AI model?",
    answer: "Model governance evaluates a model before deployment: bias, drift, robustness. Agent governance operates at runtime, on actions: was this action authorised, did it comply with policy, who approved it, and can that be proven to an auditor afterwards. Regulated deployments need both layers."
  },
  {
    question: "How can an organisation prove an AI agent's actions to a regulator?",
    answer: "Through tamper-evident records: each agent action recorded with its policy evaluation and approver, cryptographically linked in sequence, anchored to an external commitment the operator cannot rewrite, and exportable in a bundle a regulator can verify offline on their own hardware."
  },
  {
    question: "Can AI agent labor be measured for workforce and tax reporting?",
    answer: "Yes. Human-equivalent-work measurement calibrates agent output against human baselines for the same task classes, producing auditable labour-hour attribution per agent. This supports workforce-impact reporting today and prepares organisations for AI labor taxation regimes under discussion in several jurisdictions."
  },
  {
    question: "Does governance require changing existing AI agents?",
    answer: "Not necessarily. A gateway deployment intercepts agent traffic at the boundary with no agent code changes, which also makes third-party agents governable. Teams building their own agents can integrate an SDK for deeper telemetry."
  }
];

// FAQ JSON-LD
const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

// Article JSON-LD
const articleJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Implementing the MGF for Agentic AI: An Implementor's Architecture Mapping",
  "datePublished": "2026-07-01",
  "dateModified": "2026-07-03",
  "author": {
    "@type": "Organization",
    "name": "SwarmCitadel Research",
    "url": "https://swarmcitadel.com/whitepapers"
  },
  "publisher": {
    "@type": "Organization",
    "name": "SwarmCitadel",
    "url": "https://swarmcitadel.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://customer-assets.emergentagent.com/job_sentinel-hub-28/artifacts/wujg6877_SwarmCitadel-logo-mark.png"
    }
  },
  "description": "An implementor's architecture mapping for IMDA's MGF for Agentic AI: risk bounding, human accountability, audit trails, and AI agent labor attribution.",
  "image": "https://customer-assets.emergentagent.com/job_sentinel-hub-28/artifacts/t3kxaktl_fig1-governed-action-lifecycle.png"
};

export const WhitepapersPage = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [optinResearch, setOptinResearch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    try {
      const response = await fetch(`${API_URL}/api/whitepaper-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name: name || null,
          paper_id: "mgf-implementors-mapping-v0.1",
          optin_future_research: optinResearch
        })
      });

      if (response.ok) {
        setSubmitStatus("success");
        setEmail("");
        setName("");
        setOptinResearch(false);
      } else {
        const data = await response.json();
        throw new Error(data.detail || "Failed to send request");
      }
    } catch (error) {
      setSubmitStatus("error");
      setErrorMessage(error.message || "Something went wrong. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="whitepapers-page">
      <Seo
        title="Implementing Singapore's Model AI Governance Framework (MGF) for Agentic AI | SwarmCitadel Research"
        description="An implementor's architecture mapping for IMDA's MGF for Agentic AI: risk bounding, human accountability, audit trails, and AI agent labor attribution."
        path="/whitepapers"
        type="article"
      >
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Seo>
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh pointer-events-none" />
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-swarm-violet/10 rounded-full blur-[150px]" />
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="section-label">Research</div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            SwarmCitadel Research
          </h1>
          <p className="text-slate-400 text-lg mb-8">
            Technical papers on AI agent governance, compliance architecture, and regulatory frameworks.
          </p>
        </div>
      </section>

      {/* Whitepaper Entry */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-6">
          <article className="glass-card p-8 md:p-10 border-swarm-violet/20">
            {/* Paper Header */}
            <div className="flex items-start gap-4 mb-8">
              <div className="w-14 h-14 rounded-xl bg-swarm-violet/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-swarm-violet" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Implementing the MGF for Agentic AI: An Implementor&apos;s Architecture Mapping
                </h2>
                <p className="text-slate-400 text-sm">
                  SwarmCitadel Research · July 2026
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  Last updated: July 2026
                </p>
              </div>
            </div>

            {/* Summary - The public, crawlable content */}
            <div className="prose prose-invert max-w-none mb-8">
              <p className="text-slate-300 leading-relaxed">
                Singapore&apos;s Model AI Governance Framework for Agentic AI, published by IMDA and the AI Verify Foundation and substantially updated in May 2026, is the first national framework written specifically for AI systems that plan, reason, and act autonomously. It is principles-based by design: it tells deployers what must be true of their governance posture, not how to build it. That leaves a gap between the framework&apos;s four dimensions and a working system architecture.
              </p>
              <p className="text-slate-300 leading-relaxed mt-4">
                This paper closes that gap from an implementor&apos;s seat. For each MGF dimension it sets out what the framework asks, what an architecture must provide to answer it, and the questions a deployer should put to any governance vendor. It examines the May 2026 update&apos;s expanded treatment of multi-agent systemic risk, cascading failure, third-party agents, and automation bias, and argues that three properties now separate aligned architectures from the rest: cross-agent telemetry correlation, policy-defined graduated containment, and boundary-level enforcement that requires no agent code changes. It closes with a practical adoption path, from gateway deployment in front of existing agents to a four-week proof of concept.
              </p>
              <p className="text-slate-400 mt-4 italic">
                Written for compliance leaders, platform owners, and architects deploying agents in regulated environments.
              </p>
            </div>

            {/* Figure 1 */}
            <div className="mb-10">
              <img 
                src="https://customer-assets.emergentagent.com/job_sentinel-hub-28/artifacts/t3kxaktl_fig1-governed-action-lifecycle.png"
                alt="Governed AI agent action lifecycle: policy evaluation, human approval, tamper-evident audit record, offline regulator verification."
                className="w-full rounded-lg border border-swarm-border"
              />
              <p className="text-slate-500 text-sm mt-3 text-center">
                Figure 1. The governed action lifecycle. Every proposed action passes through deterministic policy evaluation before execution.{" "}
                <Link to="/governance#lifecycle" className="text-swarm-cyan hover:underline" data-testid="figure1-live-link">See it running live →</Link>
              </p>
            </div>

            {/* Lead Capture Form */}
            <div className="bg-swarm-darker/50 rounded-xl p-6 md:p-8 border border-swarm-border">
              <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                <Download className="w-5 h-5 text-swarm-cyan" />
                Get the full paper
              </h3>
              <p className="text-slate-400 text-sm mb-6">
                Enter your email below and we will send you the complete paper.
              </p>

              {submitStatus === "success" ? (
                <div className="flex items-start gap-3 p-4 bg-swarm-green/10 border border-swarm-green/30 rounded-lg" data-testid="whitepaper-success">
                  <CheckCircle className="w-5 h-5 text-swarm-green flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-swarm-green font-medium">Sent.</p>
                    <p className="text-slate-400 text-sm">Check your inbox (and spam folder) for the paper.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="whitepaper-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="wp-email" className="text-sm text-slate-400 block mb-1.5">
                        Email <span className="text-red-400">*</span>
                      </label>
                      <Input
                        id="wp-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="bg-swarm-bg border-swarm-border text-white placeholder:text-slate-500"
                        data-testid="whitepaper-email"
                      />
                    </div>
                    <div>
                      <label htmlFor="wp-name" className="text-sm text-slate-400 block mb-1.5">
                        Name <span className="text-slate-600">(optional)</span>
                      </label>
                      <Input
                        id="wp-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="bg-swarm-bg border-swarm-border text-white placeholder:text-slate-500"
                        data-testid="whitepaper-name"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="wp-optin"
                      checked={optinResearch}
                      onCheckedChange={setOptinResearch}
                      className="mt-1 border-swarm-border data-[state=checked]:bg-swarm-violet data-[state=checked]:border-swarm-violet"
                      data-testid="whitepaper-optin"
                    />
                    <label htmlFor="wp-optin" className="text-sm text-slate-400 cursor-pointer">
                      Also send me future SwarmCitadel research.
                    </label>
                  </div>

                  {submitStatus === "error" && (
                    <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="whitepaper-error">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-400 font-medium">Delivery failed.</p>
                        <p className="text-slate-400 text-sm">
                          {errorMessage} You can reach us directly via our{" "}
                          <Link to="/contact" className="text-swarm-cyan hover:underline">contact form</Link>.
                        </p>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-glow w-full md:w-auto"
                    data-testid="whitepaper-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send me the paper
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </article>
        </div>
      </section>

      {/* Related Link to Governance */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <span>Related:</span>
            <Link to="/governance" className="text-swarm-cyan hover:underline">
              How SwarmCitadel implements the MGF
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-swarm-darker/30">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div key={index} className="border-b border-swarm-border pb-6 last:border-0">
                <h3 className="text-lg font-semibold text-white mb-3">
                  {faq.question}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to implement governed AI agents?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            See how SwarmCitadel&apos;s architecture maps to the MGF dimensions in practice.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-glow" data-testid="whitepapers-cta-demo">
                Request Demo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/governance">
              <Button variant="outline" className="btn-outline" data-testid="whitepapers-cta-governance">
                View Implementation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhitepapersPage;
